/**
 * Dashboard Component
 * 
 * Main chat interface with sidebar navigation and chat area.
 * Handles user search, chat list, and settings.
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import ChatList from './ChatList';
import ChatWindow from './ChatWindow';
import UserSearch from './UserSearch';
import Settings from './Settings';

const Dashboard = ({ selectedChat, selectedUser, onSelectChat }) => {
  const { userProfile, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('chats');
  const [showSettings, setShowSettings] = useState(false);

  /**
   * Handle tab switching
   */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  /**
   * Handle chat selection from search
   */
  const handleStartChat = (chatId, user) => {
    onSelectChat(chatId, user);
    setActiveTab('chats');
  };

  return (
    <div className="h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">YT</span>
              </div>
              <h1 className="text-xl font-bold text-gray-800">YTag Chat</h1>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Settings"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c1.56.379 2.6-1.56 2.978-2.978a1.532 1.532 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.532 1.532 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* User Profile */}
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <img
              src={userProfile?.avatarURL || '/default-avatar.png'}
              alt={userProfile?.displayName}
              className="w-10 h-10 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">
                {userProfile?.displayName}
              </p>
              <p className="text-sm text-gray-600 truncate">
                {userProfile?.verifiedTag}
              </p>
            </div>
            <button
              onClick={logout}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Sign out"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => handleTabChange('chats')}
            className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
              activeTab === 'chats'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            Chats
          </button>
          <button
            onClick={() => handleTabChange('search')}
            className={`flex-1 py-3 px-4 text-center font-medium transition-colors ${
              activeTab === 'search'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            Search
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'chats' && (
            <ChatList
              onSelectChat={onSelectChat}
              selectedChatId={selectedChat}
            />
          )}
          {activeTab === 'search' && (
            <UserSearch onStartChat={handleStartChat} />
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <ChatWindow
          chatId={selectedChat}
          otherUser={selectedUser}
        />
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <Settings onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
};

Dashboard.propTypes = {
  selectedChat: PropTypes.string,
  selectedUser: PropTypes.object,
  onSelectChat: PropTypes.func.isRequired,
};

export default Dashboard;
